import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, Select, notification, Skeleton, Tag, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import { getOutOfStockItems, restockMenu, restockRecipe } from '../../services/outofstockservice';
import {
    OutOfStockItemDto,
    PagedResult,
    RestockMenuRequest,
    RestockRecipeRequest,
} from '../../types/outofstocktypes';
import { StoreDropdownDto } from '../../types/storetypes';
import { getAllStoresForDropdown } from '../../services/storeserivce';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;

interface OutofStockTabProps {
    userGuid: string;
    activeTab: string;
}

const OutOfStockTab: React.FC<OutofStockTabProps> = ({ userGuid, activeTab }) => {

    const [items, setItems] = useState<OutOfStockItemDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState<'menu' | 'recipe' | undefined>(undefined);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [stores, setStores] = useState<StoreDropdownDto[]>([]);
    const [storeFilter, setStoreFilter] = useState<string | undefined>(undefined);
    const [restockingId, setRestockingId] = useState<string | null>(null);

    const fetchItems = async (
        page = pageNumber,
        size = pageSize,
        search = searchTerm,
        f = filter,
        storeNum = storeFilter
    ) => {
        setLoading(true);
        try {
            const res = await getOutOfStockItems(page, size, search, f, storeNum);
            const data: PagedResult<OutOfStockItemDto> = res.data;
            setItems(data.items);
            setTotalCount(data.totalCount);
            setPageNumber(data.pageNumber);
            setPageSize(data.pageSize);
        } catch (err) {
            console.error(err);
            notification.error({ message: 'Error', description: 'Failed to load out-of-stock items.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'outofstock') {
            fetchItems(1, pageSize, searchTerm, filter, storeFilter); // reload on tab click
        }
    }, [activeTab]);

    useEffect(() => {
        getAllStoresForDropdown().then(res => setStores(res.data));
    }, []);

    useEffect(() => {
        fetchItems();
    }, [pageSize, filter, storeFilter]);

    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setSearchTerm(value);
            fetchItems(1, pageSize, value, filter, storeFilter);
        }, 400),
        [pageSize, filter, storeFilter]
    );

    const handleRestock = async (record: OutOfStockItemDto) => {
        setRestockingId(`${record.itemType}-${record.itemGuid}-${record.storeGuid}`);
        try {
            if (record.itemType === 'Menu') {
                const request: RestockMenuRequest = {
                    menuGuid: record.itemGuid,
                    storeGuids: [record.storeGuid],
                    userGuid,
                    reason: 'Restocked via OutOfStock tab',
                };
                await restockMenu(request);
            } else {
                const request: RestockRecipeRequest = {
                    recipeGuid: record.itemGuid,
                    storeGuids: [record.storeGuid],
                    userGuid,
                    reason: 'Restocked via OutOfStock tab',
                };
                await restockRecipe(request);
            }
            notification.success({
                message: 'Success',
                description: `${record.name} (${record.itemType}) restocked for store ${record.storeNumber}.`,
            });
            fetchItems(pageNumber, pageSize, searchTerm, filter, storeFilter);
        } catch (err) {
            console.error(err);
            notification.error({ message: 'Error', description: `Failed to restock ${record.name}.` });
        } finally {
            setRestockingId(null);
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilter(undefined);
        setStoreFilter(undefined);
        setPageNumber(1);

        fetchItems(1, pageSize, '', undefined, undefined);
    };

    const columns: ColumnsType<OutOfStockItemDto> = [
        {
            title: 'Date Created',
            dataIndex: 'dateCreated',
            key: 'dateCreated',
            render: (val: string) => dayjs(val).format('DD MMM YYYY, HH:mm'),
        },
        { title: 'Item', dataIndex: 'name', key: 'name' },
        { title: 'Type', dataIndex: 'itemType', key: 'itemType' },
        { title: 'Store Number', dataIndex: 'storeNumber', key: 'storeNumber' },
        {
            title: 'Status',
            key: 'status',
            render: () => <Tag color="red">OutOfStock</Tag>,
        },
    ];

    return (
        <>
            <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
                <Search
                    placeholder="Search items"
                    onChange={e => debouncedSearch(e.target.value)}
                    style={{ width: 300 }}
                    allowClear
                />
                <Select
                    value={filter}
                    onChange={val => setFilter(val)}
                    style={{ width: 150 }}
                    allowClear
                    placeholder="Filter by Type"
                >
                    <Option value="menu">Menus</Option>
                    <Option value="recipe">Recipes</Option>
                </Select>

                <Select
                    value={storeFilter}
                    onChange={val => {
                        setStoreFilter(val);
                        fetchItems(1, pageSize, searchTerm, filter, val);
                    }}
                    style={{ width: 250 }}
                    allowClear
                    placeholder="Filter by Store"
                    showSearch
                    optionFilterProp="value"
                    filterOption={(input, option) =>
                        (option?.value as string).toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {stores.map(s => (
                        <Option key={s.storeGuid} value={s.storeNumber}>
                            {s.storeNumber}
                        </Option>
                    ))}
                </Select>
                <Button onClick={clearFilters}>Clear Filters</Button>
            </div>

            {loading && items.length === 0 ? (
                <Skeleton active paragraph={{ rows: 5 }} />
            ) : (
                <Table<OutOfStockItemDto>
                    dataSource={items}
                    columns={columns}
                    loading={loading && items.length > 0}
                    pagination={{
                        current: pageNumber,
                        pageSize: pageSize,
                        total: totalCount,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
                        onChange: (page, size) => {
                            setPageSize(size);
                            fetchItems(page, size, searchTerm, filter, storeFilter);
                        },
                    }}
                    rowKey={record => `${record.itemType}-${record.itemGuid}-${record.storeGuid}`}
                />
            )}
        </>
    );
};

export default OutOfStockTab;